import React from 'react';
import { X, Mountain, TreePine, Navigation, MapPin, Car, Eye, Home, Droplet, Compass } from 'lucide-react';
import { latLngToMaidenhead } from '../utils/maidenhead';
import { getScoreColor, getScoreLabel, getLOSLabel } from '../utils/scoreCalculator';

export const DetailPanel = ({ point, onClose }) => {
  const maidenhead = latLngToMaidenhead(point.lat, point.lng);
  const isSummit = point.type === 'summit';
  
  const scoreColor = getScoreColor(point.score);
  const scoreLabel = getScoreLabel(point.score);

  return (
    <div 
      className="bg-zinc-950/95 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl w-80 max-h-[90vh] overflow-y-auto" 
      data-testid="detail-panel"
    >
      {/* Header */}
      <div className={`border-b border-white/10 px-5 py-4 flex items-center justify-between sticky top-0 z-10 ${
        isSummit ? 'bg-amber-500/10' : 'bg-emerald-500/10'
      }`}>
        <div className="flex items-center gap-3">
          {isSummit ? (
            <Mountain className="w-5 h-5 text-amber-500" />
          ) : (
            <TreePine className="w-5 h-5 text-emerald-500" />
          )}
          <div>
            <h2 className={`font-semibold ${isSummit ? 'text-amber-400' : 'text-emerald-400'}`}>
              {isSummit ? 'Point Haut' : 'Spot Confort'}
            </h2>
            <p className="text-zinc-400 text-xs line-clamp-1">{point.name}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded"
          data-testid="close-detail-button"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Score Display */}
      <div className="px-5 py-5 border-b border-white/10 text-center">
        <div className="text-zinc-500 uppercase text-xs tracking-widest mb-2">HamScore</div>
        <div 
          className="font-mono text-5xl font-bold" 
          style={{ color: scoreColor }}
          data-testid="point-score"
        >
          {point.score.toFixed(0)}
        </div>
        <div className="mt-2 text-sm" style={{ color: scoreColor }}>{scoreLabel}</div>
        
        {/* Score Bar */}
        <div className="mt-4 h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-500 rounded-full"
            style={{
              width: `${point.score}%`,
              backgroundColor: scoreColor
            }}
          />
        </div>
      </div>

      {/* LOS Compass - For both Summit and Comfort */}
      {point.losData && (
        <div className="px-5 py-4 border-b border-white/10">
          <label className="text-zinc-500 uppercase text-xs tracking-widest block mb-3">
            <Compass className="w-3 h-3 inline mr-1" />
            Dégagement LOS (8 directions)
          </label>
          
          {/* LOS Summary */}
          <div className="text-center mb-3">
            <span className={`text-lg font-bold ${
              point.losData.clearCount >= 6 ? 'text-emerald-400' :
              point.losData.clearCount >= 4 ? 'text-amber-400' : 'text-red-400'
            }`}>
              {point.losData.clearCount}/8 dégagé
            </span>
            <div className="text-xs text-zinc-500 mt-1">
              {getLOSLabel(point.losData.clearCount)}
            </div>
          </div>

          {/* Compass Rose */}
          <div className="relative w-full aspect-square max-w-[180px] mx-auto">
            {/* Center point */}
            <div className={`absolute top-1/2 left-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white z-10 ${
              isSummit ? 'bg-amber-500' : 'bg-emerald-500'
            }`} />
            
            {/* Direction indicators */}
            {[
              { name: 'N', top: '5%', left: '50%', transform: '-translate-x-1/2' },
              { name: 'NE', top: '15%', left: '85%', transform: '-translate-x-1/2' },
              { name: 'E', top: '50%', left: '95%', transform: '-translate-y-1/2' },
              { name: 'SE', top: '85%', left: '85%', transform: '-translate-x-1/2' },
              { name: 'S', top: '95%', left: '50%', transform: '-translate-x-1/2' },
              { name: 'SW', top: '85%', left: '15%', transform: '-translate-x-1/2' },
              { name: 'W', top: '50%', left: '5%', transform: '-translate-y-1/2' },
              { name: 'NW', top: '15%', left: '15%', transform: '-translate-x-1/2' },
            ].map(dir => {
              const dirData = point.losData.directions[dir.name];
              const isClear = dirData?.clear;
              
              return (
                <div
                  key={dir.name}
                  className={`absolute text-xs font-bold px-2 py-1 rounded ${
                    isClear 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/50'
                  }`}
                  style={{
                    top: dir.top,
                    left: dir.left,
                    transform: dir.transform
                  }}
                  title={dirData ? `Angle: ${dirData.angle?.toFixed(1)}°` : 'Non évalué'}
                >
                  {dir.name}
                </div>
              );
            })}

            {/* Connection lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
              {[
                { name: 'N', x2: 50, y2: 15 },
                { name: 'NE', x2: 78, y2: 22 },
                { name: 'E', x2: 85, y2: 50 },
                { name: 'SE', x2: 78, y2: 78 },
                { name: 'S', x2: 50, y2: 85 },
                { name: 'SW', x2: 22, y2: 78 },
                { name: 'W', x2: 15, y2: 50 },
                { name: 'NW', x2: 22, y2: 22 },
              ].map(line => {
                const dirData = point.losData.directions[line.name];
                const isClear = dirData?.clear;
                
                return (
                  <line
                    key={line.name}
                    x1="50"
                    y1="50"
                    x2={line.x2}
                    y2={line.y2}
                    stroke={isClear ? '#10b981' : '#ef4444'}
                    strokeWidth="2"
                    strokeOpacity="0.5"
                  />
                );
              })}
            </svg>
          </div>
        </div>
      )}

      {/* Maidenhead Locator */}
      <div className="px-5 py-4 border-b border-white/10">
        <label className="text-zinc-500 uppercase text-xs tracking-widest block mb-2">
          <Navigation className="w-3 h-3 inline mr-1" />
          Locator Maidenhead
        </label>
        <div className="font-mono text-cyan-400 text-2xl font-bold" data-testid="maidenhead-locator">
          {maidenhead}
        </div>
      </div>

      {/* Coordinates & Altitude */}
      <div className="px-5 py-4 border-b border-white/10">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-500 uppercase text-xs tracking-widest block mb-1">
              <MapPin className="w-3 h-3 inline mr-1" />
              Coordonnées
            </label>
            <div className="font-mono text-white text-sm" data-testid="point-latitude">
              {point.lat.toFixed(5)}°N
            </div>
            <div className="font-mono text-white text-sm" data-testid="point-longitude">
              {point.lng.toFixed(5)}°E
            </div>
          </div>
          <div>
            <label className="text-zinc-500 uppercase text-xs tracking-widest block mb-1">
              <Mountain className="w-3 h-3 inline mr-1" />
              Altitude
            </label>
            <div className="font-mono text-emerald-400 text-2xl font-bold" data-testid="point-elevation">
              {point.elevation}m
            </div>
          </div>
        </div>
      </div>

      {/* Comfort-specific details */}
      {!isSummit && (
        <div className="px-5 py-4">
          <label className="text-zinc-500 uppercase text-xs tracking-widest block mb-3">
            Équipements
          </label>
          <div className="space-y-2">
            <CriteriaItem 
              icon={Car} 
              label="Accès route" 
              value={point.amenityData?.hasRoadAccess ? 'Oui' : 'Non'}
              positive={point.amenityData?.hasRoadAccess}
            />
            <CriteriaItem 
              icon={Car} 
              label="Parking" 
              value={point.amenityData?.hasParking ? 'Disponible' : 'Non trouvé'}
              positive={point.amenityData?.hasParking}
            />
            <CriteriaItem 
              icon={Eye} 
              label="Point de vue" 
              value={point.amenityData?.hasViewpoint ? 'Oui' : 'Non'}
              positive={point.amenityData?.hasViewpoint}
            />
            <CriteriaItem 
              icon={Home} 
              label="Abri" 
              value={point.amenityData?.hasShelter ? 'Disponible' : 'Non'}
              positive={point.amenityData?.hasShelter}
            />
            <CriteriaItem 
              icon={Droplet} 
              label="Point d'eau" 
              value={point.amenityData?.hasWater ? 'Disponible' : 'Non'}
              positive={point.amenityData?.hasWater}
            />
          </div>
        </div>
      )}

      {/* Open in Maps */}
      <div className="px-5 py-4 border-t border-white/10">
        <a
          href={`https://www.google.com/maps?q=${point.lat},${point.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full block text-center py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-md transition-colors"
        >
          Ouvrir dans Google Maps
        </a>
      </div>
    </div>
  );
};

// Helper component for criteria items
const CriteriaItem = ({ icon: Icon, label, value, positive }) => (
  <div className="flex items-center justify-between py-1">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-zinc-500" />
      <span className="text-zinc-400 text-sm">{label}</span>
    </div>
    <span className={`text-xs font-medium ${positive ? 'text-emerald-400' : 'text-zinc-500'}`}>
      {value}
    </span>
  </div>
);
